/**
 * MoneyX Indexer REST client
 *
 * Single entry point for all requests to the custom indexer API.
 * Replaces Apollo/GraphQL calls to subgraph/subsquid/goldsky.
 *
 * Base URL configured via VITE_INDEXER_API (default: http://localhost:8011)
 */

export const INDEXER_BASE = (
    (import.meta.env.VITE_INDEXER_API as string | undefined) ?? "http://localhost:8011"
).replace(/\/$/, "")

async function get<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
    const fullPath = `${INDEXER_BASE}${path}`
    // Use window.location.origin as base for relative URLs (Vite proxy mode)
    const base = INDEXER_BASE.startsWith("http") ? undefined : window.location.origin
    const url = new URL(fullPath, base)
    if (params) {
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) url.searchParams.set(k, String(v))
        })
    }
    const res = await fetch(url.toString())
    if (!res.ok) {
        const body = await res.text().catch(() => "")
        throw new Error(`Indexer ${path} → ${res.status}: ${body}`)
    }
    return res.json()
}

// ── Trades ────────────────────────────────────────────────────────────────────

export type TradeRow = {
    id: number
    tx_hash: string
    block_number: number
    block_ts: number
    account: string
    collateral_token: string
    index_token: string
    collateral_delta: string
    size_delta: string
    is_long: number
    price: string
    fee: string
    action: "increase" | "decrease" | "liquidate"
    realised_pnl: string | null
}

export function fetchTrades(params: {
    account?: string
    indexToken?: string
    action?: string
    from?: number
    to?: number
    page?: number
    pageSize?: number
}) {
    return get<{ total: number; page: number; pageSize: number; items: TradeRow[] }>("/api/trades", params as any)
}

export function fetchHasTraded(account: string) {
    return get<{ hasTraded: boolean }>("/api/trades/first", { account })
}

export type SwapRow = {
    id: number
    tx_hash: string
    log_index: number
    block_number: number
    block_ts: number
    account: string
    token_in: string
    token_out: string
    amount_in: string
    amount_out: string
    amount_out_after_fees: string
    fee_basis_points: number
}

export function fetchSwaps(params: { account?: string; from?: number; to?: number; page?: number; pageSize?: number }) {
    return get<{ total: number; page: number; pageSize: number; items: SwapRow[] }>("/api/trades/swaps", params as any)
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export function fetchFeeStats(from?: number) {
    return get<{ weeklyFees: string; totalFees: string; daily: any[] }>("/api/stats/fees", from ? { from } : undefined)
}

export function fetchVolumeStats(params?: { from?: number; indexToken?: string }) {
    return get<{ dailyVolume: string; totalVolume: string; daily: any[] }>("/api/stats/volume", params as any)
}

export function fetchUserStats() {
    return get<{ totalUsers: number; activeUsers30d: number }>("/api/stats/users")
}

export function fetchMarketVolumes(from?: number) {
    return get<Record<string, string>>("/api/stats/markets/volumes", from ? { from } : undefined)
}

// ── Referrals ─────────────────────────────────────────────────────────────────

export function fetchReferralCodes(owner: string) {
    return get<{ codes: any[] }>("/api/referrals/codes", { owner })
}

export function fetchReferralCode(code: string) {
    return get<any>(`/api/referrals/code/${code}`)
}

export function fetchReferrerStats(address: string) {
    return get<any>(`/api/referrals/referrer/${address}`)
}

export function fetchTraderCode(address: string) {
    return get<{ account: string; code: string | null; referrer: string | null; setTs: number }>(`/api/referrals/trader/${address}`)
}

export function fetchCheckCodes(codes: string[]) {
    return get<Record<string, { owner: string | null; isTaken: boolean }>>("/api/referrals/check-codes", {
        codes: codes.join(","),
    })
}

// ── Account ───────────────────────────────────────────────────────────────────

export function fetchAccountStats(address: string) {
    return get<{
        account: string
        totalTrades: number
        wins: number
        losses: number
        winRate: number
        volumeUsd: string
        realisedPnl: string
        totalFeesPaid: string
        avgLeverage: number
    }>(`/api/account/${address}/stats`)
}

export function fetchPnlHistory(address: string, params?: { buckets?: number; period?: "day" | "week" | "month" }) {
    return get<{ account: string; period: string; buckets: any[] }>(`/api/account/${address}/pnl-history`, params as any)
}

// ── Leaderboard ───────────────────────────────────────────────────────────────

export function fetchLeaderboard(params?: {
    sort?: string
    limit?: number
    offset?: number
    account?: string
    from?: number
}) {
    return get<{ total: number; offset: number; limit: number; items: any[]; accountRow: any | null }>("/api/leaderboard", params as any)
}

// ── Prices ────────────────────────────────────────────────────────────────────

export function fetchCandles(token: string, params: { resolution?: string; from?: number; to?: number }) {
    return get<Array<{ time: number; open: number; high: number; low: number; close: number; volume: number }>>(
        `/api/prices/candles/${token}`,
        params as any
    )
}

export function fetchLastPrice(token: string) {
    return get<{ token: string; price: number; ts: number }>(`/api/prices/last/${token}`)
}

export function fetchLivePrices() {
    return get<Record<string, number>>("/api/prices/live")
}

// ── Claims ────────────────────────────────────────────────────────────────────

export function fetchClaims(params: { account?: string; page?: number; pageSize?: number }) {
    return get<{ total: number; page: number; pageSize: number; items: any[] }>("/api/claims", params as any)
}

// ── V2 data (empty — V2 markets not deployed on MoneyX) ───────────────────────

export function fetchV2Markets() {
    return get<{ markets: any[] }>("/api/v2/markets")
}

export function fetchV2Rebates(params?: { account?: string }) {
    return get<{ items: any[] }>("/api/v2/rebates", params as any)
}

export function fetchV2RebateGroups(params?: { page?: number; pageSize?: number; inclReviewed?: boolean }) {
    return get<{ total: number; page: number; pageSize: number; items: any[]; hasMore: boolean }>(
        "/api/v2/rebate-groups",
        params as any
    )
}

export function fetchV2Incentives(params?: { account?: string }) {
    return get<{ items: any[] }>("/api/v2/incentives", params as any)
}

// ── Orders ────────────────────────────────────────────────────────────────────

export function fetchOrders(params?: { account?: string; status?: string; page?: number; pageSize?: number }) {
    return get<{ total: number; page: number; pageSize: number; items: any[] }>("/api/orders", params as any)
}
